import React, { useEffect, useState } from "react";
import { Box, Grid, Typography, Button, CardMedia, Paper } from "@mui/material";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import LoadingSpinner from "../../components/LoadingSpinner";
import { skipToken } from '@reduxjs/toolkit/query/react';
import { useGetSocialListeningQuery, useGetYoutubeConfigurationQuery, useGetYoutubeVideosQuery } from "../../Services/instagram/Instagram";
import SocialListening from "./Instagram/SocialListening";
import SettingsIcon from '@mui/icons-material/Settings';
import IconButton from '@mui/material/IconButton';

import YoutubeSideMenu from './YoutubeSideMenu';
import TagModal from "./Instagram/TagModal";

const YoutubePage = () => {
  const [activeTab, setActiveTab] = useState<'videos' | 'socialListening'>('videos');
  const [error, setError] = useState<any>("");
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [tagInput, setTagInput] = useState('');
  const [tags, setTags] = useState<string[]>([]);
  const { data: socialListeningData, status: socialListeningStatus, refetch: refetchSocialListening } = useGetSocialListeningQuery({socialListening: "youtube"});
  // First, fetch the YouTube configuration
  const { data: youtubeData, isLoading: isYoutubeLoading } = useGetYoutubeConfigurationQuery({});

  // Then, fetch videos only when youtubeData is available
  const { data: youtubeVideosData, isLoading: isYoutubeVideosLoading } = useGetYoutubeVideosQuery(
    youtubeData?.results?.[0]?.team && youtubeData?.results?.[0]?.channel_id
      ? {
        teamId: youtubeData.results[0].team,
        channelId: youtubeData.results[0].channel_id
      }
      : skipToken, // This will skip the query if conditions aren't met
    { skip: !youtubeData?.results?.[0]?.team || !youtubeData?.results?.[0]?.channel_id }
  );

  const apiUrl = process.env.REACT_APP_GRAPHQL_API_URL?.replace(
    /\/?graphql\/?$/,
    ""
  );
  const handleOpen = () => setOpen(true);
  const handleClose = () => setOpen(false);
  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => setTagInput(e.target.value);
  const handleAddTag = () => {
    const trimmed = tagInput.trim();
    if (trimmed && !tags.includes(trimmed)) {
      setTags([...tags, trimmed]);
    }
    setTagInput('');
  };
  const handleDeleteTag = (tagToDelete: string) => setTags(tags.filter(tag => tag !== tagToDelete));

  
  // Set error if videos query fails
  useEffect(() => {
    if (youtubeVideosData?.error) {
      setError("Failed to fetch videos. Please try again later.");
    }
  }, [youtubeVideosData?.error]);

  useEffect(() => {
    if (activeTab === 'socialListening') {
      refetchSocialListening();
    }
  }, [activeTab]);
  const starterSystems = React.useMemo(() => {
  console.log("socialListeningData", socialListeningData)
  if (!socialListeningData || !Array.isArray(socialListeningData.data)) return [];
  let result = [];
  socialListeningData.data.forEach((post: any) => {
    result.push({
      image: post.thumbnail,
      media_type: "IMAGE",
      title: post.query,
      description: post.description,
      permalink: post.videoUrl,
      timestamp: post.publishedAt,
    });
  });
  return result;
}, [socialListeningData]);

  useEffect(() => {
    if (socialListeningStatus === "pending") {
      setLoading(true)
    } else {
      setLoading(false)
    }
  }, [socialListeningStatus])

  const handleVideoClick = (videoId: string) => {
    navigate(`/social-media/youtube/video/${videoId}`);
  };

  if (isYoutubeLoading || isYoutubeVideosLoading) {
    return (
      <div className="flex justify-center items-center h-64 text-lg font-semibold text-gray-700">
        <LoadingSpinner />
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex justify-center items-center h-64 text-red-600 font-medium">
        {error}
      </div>
    );
  }


  

  return (
    <div
      style={{
        display: 'flex',
        background: '#fff',
        flex: 1,
        height: '100vh',
        overflow: 'hidden',
      }}
    >
      <div style={{ flexShrink: 0, alignSelf: 'stretch' }}>
        <YoutubeSideMenu activeTab={activeTab} setActiveTab={setActiveTab} />
      </div>
      <div
        style={{
          flex: 1,
          minWidth: 0,
          height: '100vh',
          overflowY: 'auto',
          position: 'relative',
          padding: '10px 10px',
        }}
      >
        {activeTab === 'socialListening' && (
          <i
            className="bi bi-gear"
            style={{
              position: 'absolute',
              top: 35,
              right: 36,
              fontSize: 25,
              cursor: 'pointer',
              zIndex: 1000,
            }}
            onClick={handleOpen}
            tabIndex={0}
            role="button"
            aria-label="Settings"
          ></i>
        )}
        {activeTab === 'videos' && (
          <Box sx={{ p: 4 }}>
            {/* Header Row */}
            <Paper sx={{ p: 2, mb: 2 }}>
              <Grid container spacing={2}>
                <Grid item xs={1}>
                  <Typography fontWeight="bold">S.no.</Typography>
                </Grid>
                <Grid item xs={3}>
                  <Typography fontWeight="bold">Thumbnail</Typography>
                </Grid>
                <Grid item xs={3}>
                  <Typography fontWeight="bold">Video title</Typography>
                </Grid>
                <Grid item xs={2}>
                  <Typography fontWeight="bold">Published date</Typography>
                </Grid>
                <Grid item xs={3}>
                  <Typography fontWeight="bold">Comments</Typography>
                </Grid>
              </Grid>
            </Paper>

            {/* Data Rows */}
            {youtubeVideosData?.videos?.videos?.map((video: any, index: number) => (
              <Paper key={video?.video_id} sx={{ p: 2, mb: 2, cursor: 'pointer' }} onClick={() => handleVideoClick(video.video_id)}>
                <Grid container spacing={2} alignItems="center">
                  <Grid item xs={1}>
                    <Typography>{index + 1}.</Typography>
                  </Grid>

                  <Grid item xs={3}>
                    <CardMedia
                      component="img"
                      image={video.thumbnail}
                      alt={video.title}
                      sx={{ width: 120, height: 80, borderRadius: 2 }}
                    />
                  </Grid>

                  <Grid item xs={3}>
                    <Typography fontWeight="bold" sx={{ mb: 1 }}>{video.title || 'No title available'}</Typography>
                  </Grid>

                  <Grid item xs={2}>
                    <Typography>
                      {new Date(video?.published_at).toLocaleDateString()}
                    </Typography>
                  </Grid>

                  <Grid item xs={3}>
                    <Button
                      onClick={() => handleVideoClick(video?.video_id)}
                      variant="text"
                      sx={{
                        color: "blue",
                        fontWeight: "bold",
                        textTransform: "none",
                      }}
                    >
                      View comments
                    </Button>
                  </Grid>
                </Grid>
              </Paper>
            ))}
          </Box>
        )}
        {activeTab === 'socialListening' && (
          <div style={{ width: '100%' }}>
            <div style={{ maxWidth: 1200, margin: '0', padding: '20px 0 0 0', marginLeft: "30px" }}>
              <div className="fw-bold" style={{ fontSize: 26, marginBottom: 3 }}>
                Social listening
             
              </div>
              <div style={{ color: '#6d6e7b', fontSize: 14, marginBottom: 10, marginTop: 2 }}>
                Discover, organize, and manage all your Social listening insights in one place.
                Empower your team with actionable analytics and streamline your social media
                strategy using smart, AI-driven tools.
              </div>
            </div>
            <SocialListening starterSystems={starterSystems} loading={loading} />
          </div>
        )}
      </div>
      <TagModal
        open={open}
        onClose={handleClose}
        tags={tags}
        tagInput={tagInput}
        onInputChange={handleInputChange}
        onAddTag={handleAddTag}
        onDeleteTag={handleDeleteTag}
        setTags={setTags}
        refetchSocialListening={refetchSocialListening}
        socialListening="youtube"
      />
    </div>
  );
};

export default YoutubePage;
